import { memo, useState, useCallback } from 'react';
import { Copy, Check, Square, Sparkles } from 'lucide-react';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { writeClipboardText } from '@/lib/clipboard';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../../components/ui/tooltip';
import { isAiLauncherDisabled, AI_CLI_REQUIRED_REASON } from '../../../ai-chat/launchers/aiLauncherGate';
import type { QueryMessage } from '../../core/types/workspaceTypes';
import { computeQueryExecutionEnergyState } from '../utils/queryExecutionEnergy';

const PROCESSING_MESSAGE_PATTERN = /^(Executing query|Cancelling query)/i;
const EXECUTING_MESSAGE_PATTERN = /^Executing query/i;

interface MessagesPanelProps {
  messages?: QueryMessage[];
  className?: string;
  /**
   * When true, the live "Executing query…" message renders in its long-running
   * form: amber colour, label suffixed with "taking longer", and a Stop
   * affordance is shown next to it (provided `onStop` is also passed). Mirrors
   * the same affordance shown above the results grid via {@link GridStatusInline}.
   */
  isLongRunning?: boolean;
  /**
   * True while a cancel request is in flight — relabels Stop to "Stopping…"
   * and offers the force-stop in its title. Stop stays clickable so the second
   * press can escalate.
   */
  isCancelling?: boolean;
  /** Cancel the in-flight query. Required for the inline Stop button to render. */
  onStop?: () => void;
  /**
   * T13 — "AI: Explain this error", offered on each `type: 'error'` row.
   * Undefined hides the affordance entirely (Query Editor is the only caller
   * that wires this; Notebook cell results and the PL/SQL debugger's own
   * `MessagesPanel` don't pass it, so they render unchanged).
   */
  onExplainError?: (message: QueryMessage) => void;
  /** Same `isAiLauncherDisabled` gate used everywhere else in this feature. */
  aiCliAvailable?: boolean;
  aiCliDetectionLoading?: boolean;
  /**
   * Handler for a message's one-click `action` (queryErrorAction.ts). Like
   * `onExplainError`, only the Query Editor wires this — rows with an
   * `action` render a plain row everywhere else (Notebook, pinned results).
   */
  onMessageAction?: (message: QueryMessage) => void;
}

// Memoised: the Query Editor re-renders on every streaming progress tick
// (each 5k-row batch) and on settled selection changes — the message list
// shouldn't re-render along with it. Callers must pass stable callback refs
// for this to help (Notebook and the PL/SQL debugger also render this panel).
export const MessagesPanel = memo(function MessagesPanel({
  messages = [],
  className = '',
  isLongRunning = false,
  isCancelling = false,
  onStop,
  onExplainError,
  aiCliAvailable,
  aiCliDetectionLoading,
  onMessageAction,
}: MessagesPanelProps) {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = useCallback(async (message: QueryMessage) => {
    const ok = await writeClipboardText(message.content);
    if (!ok) return;
    setCopiedId(message.id);
    setTimeout(() => setCopiedId(null), 2000);
  }, []);
  if (!messages.length) {
    return (
      <div className={`w-full h-full flex items-center justify-center ${className}`} data-testid="messages-panel">
        <p className="text-muted-foreground">No messages to display.</p>
      </div>
    );
  }

  const getMessageTypeClass = (type: QueryMessage['type']) => {
    switch (type) {
      case 'error':
        return 'text-destructive';
      case 'success':
        return 'text-green-500';
      case 'warning':
        return 'text-warning';
      case 'info':
      default:
        return 'text-blue-500';
    }
  };

  return (
    <div className={`w-full h-full overflow-auto p-4 ${className}`} data-testid="messages-panel">
      <ul className="space-y-2">
        {[...messages].reverse().map((message) => {
          const isExecutingMessage = EXECUTING_MESSAGE_PATTERN.test(message.content);
          const showLongRunning = isExecutingMessage && isLongRunning;
          // CH-28 — long-running form recolours the row onto the shared
          // --energy family (computeQueryExecutionEnergyState) instead of a
          // hardcoded amber — matches the GridStatusInline above-grid colour
          // swap. This is small bold-ish text (row + label chip + Stop
          // button), so it takes --energy-text, not the glyph-scale --energy.
          const energized = computeQueryExecutionEnergyState(showLongRunning) === 'energy';
          const typeColourClass = energized
            ? 'text-energy-text'
            : getMessageTypeClass(message.type);
          const messageLabel = showLongRunning ? 'Executing query… taking longer' : message.content;
          return (
            <li
              key={message.id}
              data-testid={isExecutingMessage ? 'messages-executing-row' : undefined}
              data-long-running={showLongRunning || undefined}
              className={`p-2 border-l-2 ${typeColourClass} border-l-current bg-muted/20 rounded`}
            >
              <div className="flex items-center justify-between">
                <span className={`font-medium ${typeColourClass}`}>
                  {message.type.charAt(0).toUpperCase() + message.type.slice(1)}
                </span>
                <div className="flex items-center gap-2">
                  {message.type === 'error' && onExplainError && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          {/* Span keeps the tooltip reachable while disabled
                              (repo convention, see QuickActionsBar). */}
                          <span
                            className="inline-block"
                            tabIndex={isAiLauncherDisabled(aiCliAvailable, aiCliDetectionLoading) ? 0 : -1}
                          >
                            <button
                              type="button"
                              className="text-muted-foreground hover:text-foreground transition-colors p-0.5 rounded disabled:opacity-50 disabled:pointer-events-none"
                              title="AI: Explain this error"
                              disabled={isAiLauncherDisabled(aiCliAvailable, aiCliDetectionLoading)}
                              onClick={() => onExplainError(message)}
                              data-testid="messages-explain-error"
                            >
                              <Sparkles className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        </TooltipTrigger>
                        {isAiLauncherDisabled(aiCliAvailable, aiCliDetectionLoading) && (
                          <TooltipContent side="top">
                            <p>{AI_CLI_REQUIRED_REASON}</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground transition-colors p-0.5 rounded"
                    title="Copy message"
                    onClick={() => void handleCopy(message)}
                  >
                    {copiedId === message.id ? (
                      <Check className="h-3.5 w-3.5 text-green-500" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </button>
                  <span className="text-xs text-muted-foreground">
                    {message.timestamp.toLocaleTimeString()}
                  </span>
                </div>
              </div>
              <p className="mt-1 text-sm whitespace-pre-wrap flex items-center gap-2">
                {PROCESSING_MESSAGE_PATTERN.test(message.content) ? (
                  <RandomDotmSpinner
                    size={12}
                    className={`flex-shrink-0 ${energized ? 'text-energy' : ''}`}
                  />
                ) : null}
                <span>{messageLabel}</span>
                {showLongRunning && onStop ? (
                  <>
                    <span className="mx-1 text-muted-foreground/60">|</span>
                    <button
                      type="button"
                      onClick={onStop}
                      // Stays clickable while cancelling — the second press is
                      // the force-stop (same contract as the toolbar and the
                      // grid-status Stop).
                      className="inline-flex items-center gap-1 hover:underline text-energy-text"
                      title={isCancelling ? 'Stopping… click again to force the stop (Oracle: drops the connection so the query returns at once — the server may still finish what it already started; other engines keep their normal cancel)' : 'Stop'}
                      data-testid="messages-stop"
                    >
                      <Square className="h-3 w-3 fill-current" />
                      {isCancelling ? 'Stopping…' : 'Stop'}
                    </button>
                  </>
                ) : null}
                {message.action && onMessageAction ? (
                  <>
                    <span className="mx-1 text-muted-foreground/60">|</span>
                    <button
                      type="button"
                      onClick={() => onMessageAction(message)}
                      className="inline-flex items-center gap-1 hover:underline text-primary"
                      data-testid="messages-fix-action"
                    >
                      {message.action.label}
                    </button>
                  </>
                ) : null}
              </p>
            </li>
          );
        })}
      </ul>
    </div>
  );
});
